import { useRef } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import {
  Settings, UserPlus, FileText, Repeat, CheckCircle, Star, ArrowRight
} from "lucide-react";
import { FadeUp, motion, useInView } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

const STEPS = [
  {
    num: "01",
    icon: <Settings className="w-7 h-7 text-[#71DED6]" />,
    title: "Setup",
    headline: "Get started in 15 minutes.",
    body: "Connect your business SMS number. Add your services and pricing. Import your customer list. That's it. CleanBid is built for people who run businesses, not IT departments.",
    detail: "No complex onboarding. No month-long setup. You're sending quotes the same day.",
    color: "green",
  },
  {
    num: "02",
    icon: <UserPlus className="w-7 h-7 text-[#6B5CF6]" />,
    title: "Lead Comes In",
    headline: "Customer texts your number. CleanBid captures it.",
    body: "When a new customer reaches out — via text, contact form, or direct link — CleanBid creates a lead automatically and surfaces it in your inbox.",
    detail: "All your leads in one place. Nothing slips through.",
    color: "indigo",
  },
  {
    num: "03",
    icon: <FileText className="w-7 h-7 text-[#71DED6]" />,
    title: "Quote Goes Out",
    headline: "Build a quote in 60 seconds. Send via SMS.",
    body: "Open the lead, select your service, set the price, hit send. The customer gets a professional quote on their phone — no app download required — and can accept with one tap.",
    detail: "Most competitors take hours to reply. You'll respond in minutes.",
    color: "green",
  },
  {
    num: "04",
    icon: <Repeat className="w-7 h-7 text-[#6B5CF6]" />,
    title: "Follow-Up Runs",
    headline: "Automated sequences keep leads warm.",
    body: "If they don't respond right away, CleanBid takes over. A day-1 reminder. A day-3 value message. A day-5 final nudge. The sequence stops the moment they respond.",
    detail: "You'll close jobs you never would have thought to follow up on.",
    color: "indigo",
  },
  {
    num: "05",
    icon: <CheckCircle className="w-7 h-7 text-[#71DED6]" />,
    title: "Job Created",
    headline: "Customer accepts. Job created automatically.",
    body: "When the customer accepts the quote, CleanBid creates the job, moves it to your pipeline, and notifies your team. No manual data entry.",
    detail: "Lead to booked job with zero paperwork.",
    color: "green",
  },
  {
    num: "06",
    icon: <Star className="w-7 h-7 text-amber-400" />,
    title: "Job Complete + Review Sent",
    headline: "Mark it done. Review request goes out automatically.",
    body: "You mark the job complete. CleanBid immediately sends a review request via SMS — straight to the platform of your choice. No awkward ask. No forgetting. Just reviews.",
    detail: "The complete revenue loop. Closed automatically.",
    color: "amber",
  },
];

const TimelineStep = ({ step, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <motion.div
      ref={ref}
      data-testid={`timeline-step-${index + 1}`}
      className="flex gap-8 md:gap-12"
      initial={{ opacity: 0, x: -32 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -32 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.06 }}
    >
      {/* Left: number + line */}
      <div className="flex flex-col items-center flex-shrink-0 w-12">
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center border text-xs font-bold ${
            step.color === "green"
              ? "bg-[#71DED6]/10 border-[#71DED6]/30 text-[#71DED6]"
              : step.color === "indigo"
              ? "bg-[#6B5CF6]/10 border-[#6B5CF6]/30 text-[#6B5CF6]"
              : "bg-amber-400/10 border-amber-400/30 text-amber-400"
          }`}
        >
          {step.num}
        </div>
        {index < STEPS.length - 1 && (
          <div className="w-px flex-1 mt-3 min-h-[60px] bg-gradient-to-b from-[#1E1E2E] to-transparent" />
        )}
      </div>

      {/* Right: content */}
      <div className="pb-14 flex-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#111118] border border-[#1E1E2E] flex items-center justify-center">
            {step.icon}
          </div>
          <span className="text-xs uppercase tracking-widest text-[#8B8BA7] font-medium">{step.title}</span>
        </div>
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#F4F4F8] mb-3">
          {step.headline}
        </h3>
        <p className="text-[#8B8BA7] leading-relaxed mb-3">{step.body}</p>
        <p
          className={`text-sm font-medium ${
            step.color === "green" ? "text-[#71DED6]" :
            step.color === "indigo" ? "text-[#6B5CF6]" : "text-amber-400"
          }`}
        >
          {step.detail}
        </p>
      </div>
    </motion.div>
  );
};

export default function HowItWorksPage() {
  usePageTitle("How It Works | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Brand mark watermark — decorative, behind content */}
        <img
          src="/cleanbid-logo-transparent.png"
          alt=""
          aria-hidden="true"
          className="absolute z-0 pointer-events-none select-none w-[200px] h-[200px] md:w-[360px] md:h-[360px] opacity-[0.05]"
          style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
        />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: text */}
            <div>
              <FadeUp>
                <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">How It Works</span>
              </FadeUp>
              <FadeUp delay={60}>
                <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[#F4F4F8] mt-4 mb-6">
                  Six steps from
                  <br />
                  <span className="text-gradient-green">lead to 5 stars.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={120}>
                <p className="text-lg text-[#8B8BA7] max-w-xl leading-relaxed">
                  CleanBid handles every stage of the job cycle automatically — from the moment a customer reaches out to the moment they leave you a review.
                </p>
              </FadeUp>
            </div>
            {/* Right: photography */}
            <FadeUp delay={200}>
              <img
                src="https://cdn.searchlegends.com/how-it-works/home-services-pro.jpg"
                alt="CleanBid home service workflow"
                loading="lazy"
                className="w-full object-cover object-center rounded-2xl h-[240px] lg:h-[360px]"
                style={{ boxShadow: "0 0 0 1px rgba(113,222,214,0.2), 0 20px 60px rgba(0,0,0,0.4)" }}
              />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="pb-24" data-testid="timeline-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          {STEPS.map((step, i) => (
            <TimelineStep key={i} step={step} index={i} />
          ))}
        </div>
      </section>

      {/* Summary */}
      <section className="py-24 bg-[#111118]/40 border-t border-[#1E1E2E]" data-testid="hiw-summary-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <FadeUp className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8]">
              The full loop. Automated.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: "Setup time", val: "15 min", color: "green" },
              { label: "Quote delivery", val: "60 sec", color: "green" },
              { label: "Follow-up sequences", val: "Automated", color: "indigo" },
              { label: "Job creation", val: "1 tap", color: "green" },
              { label: "Review requests", val: "Auto on complete", color: "amber" },
              { label: "Manual work", val: "Near zero", color: "green" },
            ].map((item, i) => (
              <FadeUp key={i} delay={i * 60}>
                <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-5 text-center">
                  <p className={`text-xl font-bold ${
                    item.color === "green" ? "text-[#71DED6]" :
                    item.color === "indigo" ? "text-[#6B5CF6]" : "text-amber-400"
                  }`}>
                    {item.val}
                  </p>
                  <p className="text-xs text-[#8B8BA7] mt-1">{item.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 glow-cta" data-testid="hiw-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              See it live in 20 minutes.
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] mb-8">
              We'll walk you through every step with your actual business in mind.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hiw-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
